<script setup>
import { useRoute  } from 'vue-router';
const props = defineProps({
  menu: {
    type: Array,
    required: true,
  },
  collapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const route = useRoute();

function isActive (item) {
  if (item.id) {
    // Special handling for booking routes
    if (item.id === 'booking') {
      return route.path.startsWith('/dashboard/bookings');
    }
    // Special handling for booking setup routes
    if (item.id === 'booking-setup') {
      return route.path.startsWith('/settings/booking');
    }
    return route.meta.parent === item.id;
  }
  
  return route.path === item.linkTo;
}

function isSubmenuActive (submenu) {
  if (submenu.id) {
    // Special handling for booking routes
    if (submenu.id === 'booking') {
      return route.path.startsWith('/dashboard/bookings');
    }
    // Special handling for booking setup routes
    if (submenu.id === 'booking-setup') {
      return route.path.startsWith('/settings/booking');
    }
    return route.meta.parent === submenu.id;
  }

  return route.path === submenu.linkTo;
};

function menuClicked (item) {
  if (typeof item?.handler === 'function') {
    item.handler();
  }
  emit('close');
}
</script>

<template>
  <ul class="space-y-2">
    <template v-for="(item, index) in menu" :key="index">

      <!-- Divider -->
      <li v-if="item.type === 'divider'" class="my-4">
        <hr class="border-gray-200">
      </li>

      <!-- Solo Menu Item -->
      <li v-if="item.type === 'solo'">
        <nuxt-link 
          @click="menuClicked" 
          :to="item.linkTo" 
          class="flex items-center p-3 rounded-xl transition-all duration-200 group relative overflow-hidden"
          :class="[
            isActive(item) 
              ? 'bg-gradient-to-r from-primary/10 to-red-50 text-primary' 
              : 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-gray-900',
            collapsed ? 'justify-center' : 'gap-3'
          ]"
        >
          <!-- Icon Container -->
          <div 
            class="flex items-center justify-center transition-all duration-200 flex-shrink-0 relative"
            :class="[
              isActive(item) 
                ? 'w-10 h-10 text-primary' 
                : 'w-8 h-8 text-gray-500 group-hover:text-gray-700'
            ]"
          >
            <i :class="[item.icon, 'text-xl']"></i>
            <!-- Badge indicator for collapsed state -->
            <span 
              v-if="collapsed && item.badge" 
              class="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"
            ></span>
          </div>
          
          <!-- Text and badge (completely hidden when collapsed) -->
          <div v-show="!collapsed" class="flex items-center justify-between flex-1 whitespace-nowrap">
            <span 
              class="font-medium transition-all duration-200"
              :class="isActive(item) ? 'text-primary' : 'text-gray-700 group-hover:text-gray-900'"
            >
              {{ item.name }}
            </span>
            <!-- Badge for count -->
            <span 
              v-if="item.badge" 
              class="badge badge-sm bg-red-500 text-white border-0 font-medium min-w-[20px] h-5"
            >
              {{ item.badge }}
            </span>
          </div>
          
          <!-- Tooltip for collapsed state -->
          <div 
            v-if="collapsed"
            class="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50"
          >
            {{ item.name }}
          </div>
        </nuxt-link>
      </li>

      <!-- Group Menu Item -->
      <li v-if="item.type === 'group'" class="space-y-2">
        <!-- Group Header -->
        <div 
          v-if="!collapsed"
          class="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider"
        >
          {{ item.group }}
        </div>
        
        <!-- Group Items -->
        <ul class="space-y-1">
          <template v-for="(submenu, submenuIndex) in item.menu" :key="submenuIndex">
            <li>
              <nuxt-link 
                @click="menuClicked(submenu)" 
                :to="submenu.linkTo" 
                class="flex items-center p-3 rounded-xl transition-all duration-200 group relative overflow-hidden"
                :class="[
                  isSubmenuActive(submenu) 
                    ? 'bg-gradient-to-r from-primary/10 to-red-50 text-primary' 
                    : 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-gray-900',
                  collapsed ? 'justify-center' : 'gap-3 ml-2'
                ]"
              >
                <!-- Icon Container -->
                <div 
                  class="flex items-center justify-center transition-all duration-200 flex-shrink-0 relative"
                  :class="[
                    isSubmenuActive(submenu) 
                      ? 'w-10 h-10 text-primary' 
                      : 'w-8 h-8 text-gray-500 group-hover:text-gray-700'
                  ]"
                >
                  <i :class="[submenu.icon, 'text-xl']"></i>
                  <!-- Badge indicator for collapsed state -->
                  <span 
                    v-if="collapsed && submenu.badge" 
                    class="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"
                  ></span>
                </div>
                
                <!-- Text and Append Icon -->
                <div v-show="!collapsed" class="flex items-center justify-between flex-1 whitespace-nowrap">
                  <span 
                    class="font-medium transition-all duration-200"
                    :class="isSubmenuActive(submenu) ? 'text-primary' : 'text-gray-700 group-hover:text-gray-900'"
                  >
                    {{ submenu.name }}
                  </span>
                  
                  <div class="flex items-center gap-2">
                    <!-- Badge for count -->
                    <span 
                      v-if="submenu.badge" 
                      class="badge badge-sm bg-red-500 text-white border-0 font-medium min-w-[20px] h-5"
                    >
                      {{ submenu.badge }}
                    </span>
                    
                    <!-- Append Icon -->
                    <i 
                      v-if="submenu.iconAppend" 
                      :class="[submenu.iconAppend, 'text-lg text-gray-400 flex-shrink-0']"
                    ></i>
                  </div>
                </div>
                
                <!-- Tooltip for collapsed state -->
                <div 
                  v-if="collapsed"
                  class="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50"
                >
                  {{ submenu.name }}
                </div>
              </nuxt-link>
            </li>
          </template>
        </ul>
      </li>
    </template>
  </ul>
</template>

<style scoped>
/* Styles now handled via Tailwind classes */
</style>
